/**
 * GV100 Import Page
 *
 * Displays existing GV100 editions as cards with two import options:
 * 1. Automatic download from Destatis (recommended)
 * 2. Manual file upload
 */

import { useRef, useEffect, useState, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Upload,
  FileText,
  Settings,
  Play,
  CheckCircle2,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  X,
  Calendar,
  Database,
  Search,
  Loader2,
  Download,
  RefreshCw,
  Globe,
  FolderOpen,
} from 'lucide-react'
import { useImportStore, type ImportStep } from '../stores/importStore'
import { useUploadGV100, useExecuteImport, useEditions } from '../hooks/queries'
import {
  useGV100AvailableVersions,
  useGV100StartDownload,
  useGV100DownloadStatus,
  useGV100ImportDownloaded,
} from '../hooks/gv100Queries'
import type { ImportExecuteParams, ImportResult, EditionInfo } from '../types'
import type { GV100VersionInfo } from '../services/gv100Api'
import { cn } from '../../lib/utils'

const STEPS: { id: ImportStep; label: string; icon: React.ReactNode }[] = [
  { id: 'upload', label: 'Datei-Upload', icon: <Upload className="w-5 h-5" /> },
  { id: 'preview', label: 'Vorschau', icon: <FileText className="w-5 h-5" /> },
  { id: 'configure', label: 'Konfiguration', icon: <Settings className="w-5 h-5" /> },
  { id: 'progress', label: 'Import', icon: <Play className="w-5 h-5" /> },
  { id: 'complete', label: 'Abschluss', icon: <CheckCircle2 className="w-5 h-5" /> },
]

// ============================================================================
// Step Indicator (compact version for dialogs)
// ============================================================================

function StepIndicator({ currentStep }: { currentStep: ImportStep }) {
  const currentIndex = STEPS.findIndex((s) => s.id === currentStep)
  const currentStepData = STEPS[currentIndex]

  return (
    <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
      <div className="flex items-center gap-1.5">
        {STEPS.map((step, index) => {
          const isCompleted = index < currentIndex
          const isCurrent = index === currentIndex

          return (
            <div
              key={step.id}
              className={cn(
                'w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition-colors',
                isCompleted && 'bg-green-600 text-white',
                isCurrent && 'bg-blue-600 text-white',
                !isCompleted && !isCurrent && 'bg-gray-200 text-gray-500'
              )}
            >
              {isCompleted ? <CheckCircle2 className="w-3.5 h-3.5" /> : index + 1}
            </div>
          )
        })}
      </div>
      <span className="text-sm font-medium text-gray-700">
        {currentStepData?.label}
      </span>
    </div>
  )
}

// ============================================================================
// Destatis Download Section
// ============================================================================

type DownloadStep = 'select' | 'downloading' | 'configure' | 'importing' | 'complete'

interface DownloadState {
  step: DownloadStep
  selectedYear: number | null
  downloadJobId: string | null
  importJobId: string | null
  adPath: string | null
  nadPath: string | null
  error: string | null
}

function DestatisDownloadSection({ onComplete, highlightYear }: { onComplete: () => void; highlightYear?: number | null }) {
  const [state, setState] = useState<DownloadState>({
    step: 'select',
    selectedYear: null,
    downloadJobId: null,
    importJobId: null,
    adPath: null,
    nadPath: null,
    error: null,
  })
  const [editionMode, setEditionMode] = useState<'create_new' | 'update_existing'>('create_new')
  const [targetEditionId, setTargetEditionId] = useState<number | null>(null)

  const { data: versionsData, isLoading: versionsLoading, refetch: refetchVersions } =
    useGV100AvailableVersions()
  const { data: editionsData } = useEditions('GV100')
  const startDownloadMutation = useGV100StartDownload()
  const { data: downloadStatus } = useGV100DownloadStatus(
    state.downloadJobId,
    state.step === 'downloading'
  )
  const importMutation = useGV100ImportDownloaded()

  // Handle download completion
  useEffect(() => {
    if (downloadStatus?.status === 'completed' && downloadStatus.ad_path) {
      setState((prev) => ({
        ...prev,
        step: 'configure',
        adPath: downloadStatus.ad_path,
        nadPath: downloadStatus.nad_path,
      }))
    } else if (downloadStatus?.status === 'failed') {
      setState((prev) => ({
        ...prev,
        step: 'select',
        error: downloadStatus.error || 'Download fehlgeschlagen',
        downloadJobId: null,
      }))
    }
  }, [downloadStatus])

  const handleStartDownload = async (year: number) => {
    setState((prev) => ({
      ...prev,
      selectedYear: year,
      error: null,
    }))

    try {
      const response = await startDownloadMutation.mutateAsync(year)
      setState((prev) => ({
        ...prev,
        step: 'downloading',
        downloadJobId: response.job_id,
      }))
    } catch (err) {
      setState((prev) => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Download konnte nicht gestartet werden',
      }))
    }
  }

  const handleStartImport = async () => {
    if (!state.adPath) return

    setState((prev) => ({ ...prev, step: 'importing', error: null }))

    try {
      const response = await importMutation.mutateAsync({
        ad_path: state.adPath,
        nad_path: state.nadPath,
        edition_mode: editionMode,
        target_edition_id: targetEditionId || undefined,
        background: true,
      })

      if (response.job_id) {
        setState((prev) => ({
          ...prev,
          step: 'complete',
          importJobId: response.job_id ?? null,
        }))
      }
    } catch (err) {
      setState((prev) => ({
        ...prev,
        step: 'configure',
        error: err instanceof Error ? err.message : 'Import fehlgeschlagen',
      }))
    }
  }

  const handleClose = () => {
    setState({
      step: 'select',
      selectedYear: null,
      downloadJobId: null,
      importJobId: null,
      adPath: null,
      nadPath: null,
      error: null,
    })
    onComplete()
  }

  // Version selection step
  if (state.step === 'select') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Verfügbare Versionen</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Wählen Sie eine Version zum Herunterladen
            </p>
          </div>
          <button
            onClick={() => refetchVersions()}
            disabled={versionsLoading}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
            title="Verfügbarkeit prüfen"
          >
            <RefreshCw className={cn('w-4 h-4', versionsLoading && 'animate-spin')} />
          </button>
        </div>

        {state.error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-800 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{state.error}</span>
            </div>
          </div>
        )}

        {versionsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            <span className="ml-2 text-sm text-gray-500">Prüfe Verfügbarkeit bei destatis.de...</span>
          </div>
        ) : versionsData?.versions && versionsData.versions.length > 0 ? (
          <div className="space-y-2 max-h-72 overflow-auto">
            {versionsData.versions.map((version) => (
              <VersionRow
                key={version.year}
                version={version}
                onDownload={handleStartDownload}
                isDownloading={startDownloadMutation.isPending && state.selectedYear === version.year}
                highlighted={highlightYear != null && version.year === highlightYear}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Globe className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">Keine Versionen verfügbar</p>
          </div>
        )}
      </div>
    )
  }

  // Downloading step
  if (state.step === 'downloading') {
    return (
      <div className="space-y-4">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-3">
            <Download className="h-6 w-6 text-blue-600 animate-pulse" />
          </div>
          <h3 className="text-base font-semibold text-gray-900">
            Download läuft...
          </h3>
          <p className="text-gray-500 text-sm mt-1">
            Edition {state.selectedYear ? state.selectedYear + 1 : ''} wird heruntergeladen
          </p>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-gray-600">{downloadStatus?.message || 'Verbinde...'}</span>
            <span className="font-medium text-gray-900">
              {downloadStatus?.progress || 0}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${downloadStatus?.progress || 0}%` }}
            />
          </div>
        </div>
      </div>
    )
  }

  // Configure step
  if (state.step === 'configure') {
    const selectedVersion = versionsData?.versions.find((v) => v.year === state.selectedYear)

    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Download abgeschlossen</h3>
          <p className="text-gray-500 text-sm mt-1">
            Konfigurieren Sie den Import für Edition {selectedVersion?.edition_name}
          </p>
        </div>

        {state.error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-800 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{state.error}</span>
            </div>
          </div>
        )}

        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-center gap-2 text-green-800 text-sm">
            <CheckCircle2 className="w-4 h-4" />
            <span>Dateien erfolgreich heruntergeladen</span>
          </div>
          <div className="mt-2 text-xs text-green-700 space-y-1">
            <p>GV100AD: {state.adPath?.split('/').pop()}</p>
            {state.nadPath && <p>GV100NAD: {state.nadPath.split('/').pop()}</p>}
          </div>
        </div>

        {/* Edition Mode Selection */}
        <div className="space-y-3">
          <h4 className="font-medium text-sm text-gray-900">Edition</h4>

          <label
            className={cn(
              'block p-3 border-2 rounded-lg cursor-pointer transition-colors',
              editionMode === 'create_new'
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            )}
          >
            <div className="flex items-start gap-3">
              <input
                type="radio"
                name="editionMode"
                value="create_new"
                checked={editionMode === 'create_new'}
                onChange={() => setEditionMode('create_new')}
                className="mt-1"
              />
              <div>
                <div className="font-medium text-sm text-gray-900">
                  Neue Edition erstellen (Empfohlen)
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  Erstellt Edition "{selectedVersion?.edition_name}"
                </p>
              </div>
            </div>
          </label>

          {editionsData?.editions && editionsData.editions.length > 0 && (
            <label
              className={cn(
                'block p-3 border-2 rounded-lg cursor-pointer transition-colors',
                editionMode === 'update_existing'
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              )}
            >
              <div className="flex items-start gap-3">
                <input
                  type="radio"
                  name="editionMode"
                  value="update_existing"
                  checked={editionMode === 'update_existing'}
                  onChange={() => setEditionMode('update_existing')}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm text-gray-900">
                    Bestehende Edition aktualisieren
                  </div>
                  {editionMode === 'update_existing' && (
                    <select
                      value={targetEditionId ?? ''}
                      onChange={(e) =>
                        setTargetEditionId(e.target.value ? Number(e.target.value) : null)
                      }
                      className="mt-2 w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="">Edition wählen...</option>
                      {editionsData.editions.map((edition) => (
                        <option key={edition.id} value={edition.id}>
                          {edition.edition} ({edition.reference_date_display})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            </label>
          )}
        </div>

        <div className="flex justify-between pt-2">
          <button
            onClick={() => setState((prev) => ({ ...prev, step: 'select', error: null }))}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück
          </button>
          <button
            onClick={handleStartImport}
            disabled={editionMode === 'update_existing' && !targetEditionId}
            className={cn(
              'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm',
              editionMode === 'update_existing' && !targetEditionId
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            )}
          >
            Import starten
            <Play className="w-4 h-4" />
          </button>
        </div>
      </div>
    )
  }

  // Importing step
  if (state.step === 'importing') {
    return (
      <div className="space-y-4">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-3">
            <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
          </div>
          <h3 className="text-base font-semibold text-gray-900">
            Import wird gestartet...
          </h3>
          <p className="text-gray-500 text-sm mt-1">
            Bitte warten Sie, während der Import-Job erstellt wird.
          </p>
        </div>
      </div>
    )
  }

  // Complete step
  if (state.step === 'complete') {
    return (
      <div className="space-y-4">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-3">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-base font-semibold text-gray-900">Job gestartet!</h3>
          <p className="text-gray-500 text-sm mt-1">
            Der Import-Job wurde erfolgreich erstellt.
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-800">
            <strong>Job-ID:</strong> {state.importJobId}
          </p>
          <p className="text-xs text-blue-600 mt-1">
            Sie können den Fortschritt im Dashboard oder unter Jobs verfolgen.
          </p>
        </div>

        <div className="flex justify-center pt-2">
          <button
            onClick={handleClose}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            Schließen
          </button>
        </div>
      </div>
    )
  }

  return null
}

function VersionRow({
  version,
  onDownload,
  isDownloading,
  highlighted = false,
}: {
  version: GV100VersionInfo
  onDownload: (year: number) => void
  isDownloading: boolean
  highlighted?: boolean
}) {
  return (
    <div
      ref={highlighted ? (el) => el?.scrollIntoView({ block: 'nearest' }) : undefined}
      className={cn(
        'flex items-center justify-between p-3 rounded-lg border',
        version.is_installed
          ? 'bg-green-50 border-green-200'
          : 'bg-white border-gray-200 hover:border-gray-300',
        highlighted && 'ring-2 ring-blue-500 border-blue-400'
      )}
    >
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">Edition {version.edition_name}</span>
          {version.is_installed && (
            <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
              Installiert
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-0.5">
          Gebietsstand: {version.reference_date}
        </p>
        {version.is_installed && version.installed_date && (
          <p className="text-xs text-gray-400 mt-0.5">
            Importiert am {new Date(version.installed_date).toLocaleDateString('de-DE')}
          </p>
        )}
      </div>
      <div>
        {version.is_installed ? (
          <span className="text-xs text-green-600">
            <CheckCircle2 className="w-4 h-4" />
          </span>
        ) : (
          <button
            onClick={() => onDownload(version.year)}
            disabled={isDownloading}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm transition-colors',
              isDownloading
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            )}
          >
            {isDownloading ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Wird geladen...
              </>
            ) : (
              <>
                <Download className="w-3.5 h-3.5" />
                Herunterladen
              </>
            )}
          </button>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Upload Step
// ============================================================================

function UploadStep() {
  const { adFile, nadFile, setFiles, setStep, setUploadResponse, setUploading, setError } =
    useImportStore()
  const uploadMutation = useUploadGV100()
  const adInputRef = useRef<HTMLInputElement>(null)
  const nadInputRef = useRef<HTMLInputElement>(null)
  const [isDraggingAd, setIsDraggingAd] = useState(false)
  const [isDraggingNad, setIsDraggingNad] = useState(false)

  const handleAdFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    setFiles(file, nadFile)
    setError(null)
  }

  const handleNadFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    setFiles(adFile, file)
  }

  const handleAdDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingAd(true)
  }

  const handleAdDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingAd(false)
  }

  const handleAdDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingAd(false)
    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile) {
      setFiles(droppedFile, nadFile)
      setError(null)
    }
  }

  const handleNadDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingNad(true)
  }

  const handleNadDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingNad(false)
  }

  const handleNadDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingNad(false)
    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile) {
      setFiles(adFile, droppedFile)
    }
  }

  const handleUpload = async () => {
    if (!adFile) {
      setError('Bitte wählen Sie eine GV100AD-Datei aus')
      return
    }

    setUploading(true)
    setError(null)

    try {
      const response = await uploadMutation.mutateAsync({
        adFile,
        nadFile: nadFile || undefined,
      })
      setUploadResponse(response)
      setStep('preview')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload fehlgeschlagen')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-gray-900">Dateien hochladen</h2>
        <p className="text-gray-500 text-sm mt-1">
          Laden Sie die GV100AD-Datei (erforderlich) und optional die GV100NAD-Datei hoch.
        </p>
      </div>

      {/* AD File */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          GV100AD-Datei (Administrative Gebietseinheiten) *
        </label>
        <div
          className={cn(
            'border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors',
            adFile ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-gray-400',
            isDraggingAd && 'border-blue-500 bg-blue-50'
          )}
          onClick={() => adInputRef.current?.click()}
          onDragOver={handleAdDragOver}
          onDragLeave={handleAdDragLeave}
          onDrop={handleAdDrop}
        >
          <input
            ref={adInputRef}
            type="file"
            accept=".txt"
            className="hidden"
            onChange={handleAdFileChange}
          />
          {adFile ? (
            <div className="flex items-center justify-center gap-2 text-green-700">
              <FileText className="w-5 h-5" />
              <span className="text-sm">{adFile.name}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setFiles(null, nadFile)
                }}
                className="ml-2 p-1 hover:bg-green-100 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="text-gray-500">
              <Upload className="w-6 h-6 mx-auto mb-2" />
              <p className="text-sm">Klicken oder Datei hierher ziehen</p>
              <p className="text-xs mt-1">GV100AD_*.txt</p>
            </div>
          )}
        </div>
      </div>

      {/* NAD File */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          GV100NAD-Datei (Nichtadministrative Gebietseinheiten)
        </label>
        <div
          className={cn(
            'border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors',
            nadFile ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-gray-400',
            isDraggingNad && 'border-blue-500 bg-blue-50'
          )}
          onClick={() => nadInputRef.current?.click()}
          onDragOver={handleNadDragOver}
          onDragLeave={handleNadDragLeave}
          onDrop={handleNadDrop}
        >
          <input
            ref={nadInputRef}
            type="file"
            accept=".txt"
            className="hidden"
            onChange={handleNadFileChange}
          />
          {nadFile ? (
            <div className="flex items-center justify-center gap-2 text-green-700">
              <FileText className="w-5 h-5" />
              <span className="text-sm">{nadFile.name}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setFiles(adFile, null)
                }}
                className="ml-2 p-1 hover:bg-green-100 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="text-gray-500">
              <Upload className="w-6 h-6 mx-auto mb-2" />
              <p className="text-sm">Klicken oder Datei hierher ziehen</p>
              <p className="text-xs mt-1">GV100NAD_*.txt (optional)</p>
            </div>
          )}
        </div>
      </div>

      {/* Upload Button */}
      <div className="flex justify-end pt-2">
        <button
          onClick={handleUpload}
          disabled={!adFile || uploadMutation.isPending}
          className={cn(
            'inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors text-sm',
            adFile && !uploadMutation.isPending
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          )}
        >
          {uploadMutation.isPending ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              Wird hochgeladen...
            </>
          ) : (
            <>
              Weiter
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </div>
  )
}

// ============================================================================
// Preview Step
// ============================================================================

function PreviewStep() {
  const { uploadResponse, setStep } = useImportStore()

  if (!uploadResponse) return null

  const { preview } = uploadResponse

  // Map Satzart codes to labels
  const satzartLabels: Record<string, string> = {
    '10': 'Bundesländer',
    '20': 'Regierungsbezirke',
    '30': 'Regionen (BW)',
    '40': 'Kreise',
    '41': 'Kreise (NAD)',
    '50': 'Gemeindeverbände',
    '60': 'Gemeinden',
    '61': 'Gemeinden (NAD)',
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-gray-900">Vorschau</h2>
        <p className="text-gray-500 text-sm mt-1">
          Überprüfen Sie die erkannten Daten vor dem Import.
        </p>
      </div>

      {/* Reference Date */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-center gap-2 text-blue-800">
          <FileText className="w-4 h-4" />
          <span className="font-medium text-sm">Gebietsstand: {preview.reference_date_display}</span>
        </div>
        <p className="text-blue-700 text-xs mt-1">
          Referenzjahr: {preview.reference_year}
        </p>
      </div>

      {/* Record Counts */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
          <h3 className="font-medium text-sm text-gray-900">Datensätze nach Satzart</h3>
        </div>
        <div className="divide-y divide-gray-200 max-h-48 overflow-auto">
          {Object.entries(preview.record_counts)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([satzart, count]) => (
              <div key={satzart} className="px-3 py-2 flex justify-between items-center text-sm">
                <div>
                  <span className="font-mono text-xs text-gray-500">Satzart {satzart}</span>
                  <span className="ml-2 text-gray-700">
                    {satzartLabels[satzart] || 'Unbekannt'}
                  </span>
                </div>
                <span className="font-medium text-gray-900">
                  {count.toLocaleString('de-DE')}
                </span>
              </div>
            ))}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-2">
        <button
          onClick={() => setStep('upload')}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Zurück
        </button>
        <button
          onClick={() => setStep('configure')}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Weiter
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

// ============================================================================
// Configure Step
// ============================================================================

function ConfigureStep() {
  const {
    uploadResponse,
    importMode,
    setImportMode,
    editionMode,
    setEditionMode,
    editionName,
    setEditionName,
    targetEditionId,
    setTargetEditionId,
    setStep,
  } = useImportStore()
  const { data: editionsData, isLoading: editionsLoading } = useEditions('GV100')

  // Auto-suggest edition name from reference year
  const suggestedEditionName = uploadResponse?.preview
    ? String(uploadResponse.preview.reference_year + 1)
    : ''

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-gray-900">Konfiguration</h2>
        <p className="text-gray-500 text-sm mt-1">
          Wählen Sie die Edition und den Import-Modus.
        </p>
      </div>

      {/* Edition Selection */}
      <div className="space-y-3">
        <h3 className="font-medium text-sm text-gray-900">Edition</h3>

        {/* Existing Editions */}
        <label
          className={cn(
            'block p-3 border-2 rounded-lg cursor-pointer transition-colors',
            editionMode === 'update_existing'
              ? 'border-blue-600 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300'
          )}
        >
          <div className="flex items-start gap-3">
            <input
              type="radio"
              name="editionMode"
              value="update_existing"
              checked={editionMode === 'update_existing'}
              onChange={() => setEditionMode('update_existing')}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="font-medium text-sm text-gray-900">Bestehende Edition aktualisieren</div>
              {editionMode === 'update_existing' && (
                <div className="mt-2">
                  {editionsLoading ? (
                    <div className="text-xs text-gray-400">Editionen werden geladen...</div>
                  ) : editionsData?.editions && editionsData.editions.length > 0 ? (
                    <select
                      value={targetEditionId ?? ''}
                      onChange={(e) =>
                        setTargetEditionId(e.target.value ? Number(e.target.value) : null)
                      }
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Edition wählen...</option>
                      {editionsData.editions.map((edition) => (
                        <option key={edition.id} value={edition.id}>
                          {edition.edition} ({edition.reference_date_display})
                          {edition.is_latest && ' - aktuell'}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                      Keine bestehenden Editionen vorhanden.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </label>

        {/* New Edition */}
        <label
          className={cn(
            'block p-3 border-2 rounded-lg cursor-pointer transition-colors',
            editionMode === 'create_new'
              ? 'border-blue-600 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300'
          )}
        >
          <div className="flex items-start gap-3">
            <input
              type="radio"
              name="editionMode"
              value="create_new"
              checked={editionMode === 'create_new'}
              onChange={() => setEditionMode('create_new')}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="font-medium text-sm text-gray-900">Neue Edition erstellen</div>
              {editionMode === 'create_new' && (
                <div className="mt-2">
                  <input
                    type="text"
                    value={editionName}
                    onChange={(e) => setEditionName(e.target.value)}
                    placeholder={suggestedEditionName || 'z.B. 2025, 2025-Q2'}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Leer lassen für automatische Benennung ({suggestedEditionName})
                  </p>
                </div>
              )}
            </div>
          </div>
        </label>
      </div>

      {/* Import Mode */}
      <div className="space-y-3">
        <h3 className="font-medium text-sm text-gray-900">Import-Modus</h3>

        <label
          className={cn(
            'block p-3 border-2 rounded-lg cursor-pointer transition-colors',
            importMode === 'upsert'
              ? 'border-blue-600 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300'
          )}
        >
          <div className="flex items-start gap-3">
            <input
              type="radio"
              name="importMode"
              value="upsert"
              checked={importMode === 'upsert'}
              onChange={() => setImportMode('upsert')}
              className="mt-1"
            />
            <div>
              <div className="font-medium text-sm text-gray-900">Aktualisieren (Upsert)</div>
              <p className="text-xs text-gray-500 mt-0.5">
                Bestehende Einträge aktualisieren, neue hinzufügen. (Empfohlen)
              </p>
            </div>
          </div>
        </label>

        <label
          className={cn(
            'block p-3 border-2 rounded-lg cursor-pointer transition-colors',
            importMode === 'replace'
              ? 'border-blue-600 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300'
          )}
        >
          <div className="flex items-start gap-3">
            <input
              type="radio"
              name="importMode"
              value="replace"
              checked={importMode === 'replace'}
              onChange={() => setImportMode('replace')}
              className="mt-1"
            />
            <div>
              <div className="font-medium text-sm text-gray-900">Ersetzen (Replace)</div>
              <p className="text-xs text-gray-500 mt-0.5">
                Alle bestehenden Daten löschen und neu importieren.
                <span className="text-red-600 font-medium"> Vorsicht: Datenverlust möglich!</span>
              </p>
            </div>
          </div>
        </label>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-2">
        <button
          onClick={() => setStep('preview')}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Zurück
        </button>
        <button
          onClick={() => setStep('progress')}
          disabled={
            editionMode === 'update_existing' &&
            !targetEditionId &&
            editionsData?.editions &&
            editionsData.editions.length > 0
          }
          className={cn(
            'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm',
            editionMode === 'update_existing' &&
              !targetEditionId &&
              editionsData?.editions &&
              editionsData.editions.length > 0
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          )}
        >
          Job starten
          <Play className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

// ============================================================================
// Progress Step
// ============================================================================

function ProgressStep({ onComplete }: { onComplete: () => void }) {
  const {
    uploadResponse,
    importMode,
    editionMode,
    editionName,
    targetEditionId,
    setStep,
    setImportResult,
    setImporting,
    setError,
    reset,
  } = useImportStore()
  const executeMutation = useExecuteImport()
  const hasStartedRef = useRef(false)
  const [jobStarted, setJobStarted] = useState(false)
  const [jobId, setJobId] = useState<string | null>(null)

  useEffect(() => {
    // Only start once
    if (hasStartedRef.current) return
    if (!uploadResponse?.upload_id) return

    hasStartedRef.current = true

    const runImport = async () => {
      setImporting(true)
      setError(null)

      // Build import params with edition settings
      const params: ImportExecuteParams = {
        mode: importMode,
        edition_mode: editionMode,
      }

      if (editionMode === 'create_new' && editionName) {
        params.edition_name = editionName
      } else if (editionMode === 'update_existing' && targetEditionId) {
        params.target_edition_id = targetEditionId
      }

      try {
        const result = await executeMutation.mutateAsync({
          uploadId: uploadResponse.upload_id,
          params,
        })

        // Check if result has a job_id (background processing) or direct result
        if (result.status === 'queued' && result.job_id) {
          // Background job started - show success and allow new import
          setJobId(result.job_id)
          setJobStarted(true)
          setImporting(false)
        } else if (result.status === 'completed' || result.total_records !== undefined) {
          // Direct result (sync execution)
          setImportResult(result as ImportResult)
          setStep('complete')
          setImporting(false)
        } else {
          // Unknown response
          throw new Error('Unerwartete Server-Antwort')
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Import fehlgeschlagen')
        setStep('configure')
        setImporting(false)
      }
    }

    runImport()
  }, [uploadResponse, importMode, editionMode, editionName, targetEditionId, executeMutation, setImporting, setError, setImportResult, setStep])

  const handleNewImport = () => {
    reset()
    onComplete()
  }

  // Job started successfully - show confirmation
  if (jobStarted && jobId) {
    return (
      <div className="space-y-4">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-3">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-base font-semibold text-gray-900">Job gestartet!</h2>
          <p className="text-gray-500 text-sm mt-1">
            Der Import-Job wurde erfolgreich in die Warteschlange gestellt.
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-800">
            <strong>Job-ID:</strong> {jobId}
          </p>
          <p className="text-xs text-blue-600 mt-1">
            Sie können den Fortschritt im Dashboard oder unter Jobs verfolgen.
          </p>
        </div>

        <div className="flex justify-center pt-2">
          <button
            onClick={handleNewImport}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            Schließen
          </button>
        </div>
      </div>
    )
  }

  // Still starting the job
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-gray-900">Job wird gestartet...</h2>
        <p className="text-gray-500 text-sm mt-1">
          Bitte warten Sie, während der Job erstellt wird.
        </p>
      </div>

      <div className="flex flex-col items-center justify-center py-6">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600" />
        <p className="mt-4 text-sm text-gray-600">Import-Job wird erstellt...</p>
      </div>
    </div>
  )
}

// ============================================================================
// Complete Step
// ============================================================================

function CompleteStep({ onComplete }: { onComplete: () => void }) {
  const { importResult, reset } = useImportStore()

  const handleFinish = () => {
    reset()
    onComplete()
  }

  const handleNewImport = () => {
    reset()
  }

  if (!importResult) return null

  const isSuccess = importResult.status === 'completed'

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-gray-900">
          {isSuccess ? 'Import abgeschlossen' : 'Import fehlgeschlagen'}
        </h2>
      </div>

      {/* Status Banner */}
      <div
        className={cn(
          'p-3 rounded-lg border',
          isSuccess
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
        )}
      >
        <div className="flex items-center gap-2 text-sm">
          {isSuccess ? (
            <CheckCircle2 className="w-4 h-4" />
          ) : (
            <AlertCircle className="w-4 h-4" />
          )}
          <span className="font-medium">
            {isSuccess
              ? `${importResult.successful_records.toLocaleString('de-DE')} Datensätze erfolgreich importiert`
              : 'Import konnte nicht abgeschlossen werden'}
          </span>
        </div>
      </div>

      {/* Edition Info */}
      {importResult.edition_name && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center gap-2 text-sm text-blue-800">
            <FileText className="w-4 h-4" />
            <span className="font-medium">
              Edition: {importResult.edition_name}
              {importResult.edition_mode === 'create_new' && ' (neu erstellt)'}
            </span>
          </div>
          <p className="text-blue-700 text-xs mt-1">Referenzjahr: {importResult.reference_year}</p>
        </div>
      )}

      {/* Stats */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
          <h3 className="font-medium text-sm text-gray-900">Import-Statistiken</h3>
        </div>
        <div className="p-3 grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-xs text-gray-500">Gesamt</p>
            <p className="font-medium">
              {importResult.total_records.toLocaleString('de-DE')}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Erfolgreich</p>
            <p className="font-medium text-green-600">
              {importResult.successful_records.toLocaleString('de-DE')}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Fehlgeschlagen</p>
            <p className="font-medium text-red-600">
              {importResult.failed_records.toLocaleString('de-DE')}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Übersprungen</p>
            <p className="font-medium text-yellow-600">
              {importResult.skipped_records.toLocaleString('de-DE')}
            </p>
          </div>
        </div>
      </div>

      {/* Errors */}
      {importResult.errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <h3 className="font-medium text-sm text-red-800 mb-2">Fehler</h3>
          <ul className="text-xs text-red-700 space-y-1 max-h-32 overflow-auto">
            {importResult.errors.slice(0, 10).map((error, i) => (
              <li key={i}>{error}</li>
            ))}
            {importResult.errors.length > 10 && (
              <li className="text-red-500">
                ... und {importResult.errors.length - 10} weitere
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-2">
        <button
          onClick={handleNewImport}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <Upload className="w-4 h-4" />
          Neuer Import
        </button>
        <button
          onClick={handleFinish}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Schließen
        </button>
      </div>
    </div>
  )
}

// ============================================================================
// Import Dialog with Tabs
// ============================================================================

type ImportTab = 'destatis' | 'manual'

function ImportDialog({
  isOpen,
  onClose,
  onImportComplete,
  initialTab = 'destatis',
  initialYear = null,
}: {
  isOpen: boolean
  onClose: () => void
  onImportComplete: () => void
  initialTab?: ImportTab
  initialYear?: number | null
}) {
  const { step, error, reset } = useImportStore()
  const [activeTab, setActiveTab] = useState<ImportTab>(initialTab)

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleComplete = () => {
    onImportComplete()
    handleClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
          <div>
            <h2 className="text-lg font-semibold">GV100 Import</h2>
            <p className="text-sm text-gray-500">Gemeindeverzeichnis importieren</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('destatis')}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors',
              activeTab === 'destatis'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            )}
          >
            <Globe className="w-4 h-4" />
            Von Destatis
            <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
              Empfohlen
            </span>
          </button>
          <button
            onClick={() => setActiveTab('manual')}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors',
              activeTab === 'manual'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            )}
          >
            <FolderOpen className="w-4 h-4" />
            Manuelle Dateien
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {activeTab === 'destatis' ? (
            <DestatisDownloadSection onComplete={handleComplete} highlightYear={initialYear} />
          ) : (
            <>
              <StepIndicator currentStep={step} />

              {/* Error Display */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 text-red-800 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>{error}</span>
                  </div>
                </div>
              )}

              {/* Step Content */}
              {step === 'upload' && <UploadStep />}
              {step === 'preview' && <PreviewStep />}
              {step === 'configure' && <ConfigureStep />}
              {step === 'progress' && <ProgressStep onComplete={handleComplete} />}
              {step === 'complete' && <CompleteStep onComplete={handleComplete} />}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Edition Card
// ============================================================================

function EditionCard({ edition }: { edition: EditionInfo }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
              {edition.provider}
            </span>
            {edition.is_latest && (
              <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                Aktuell
              </span>
            )}
          </div>
          <h3 className="font-semibold text-gray-900">
            Edition {edition.edition}
          </h3>
        </div>
      </div>

      {/* Reference Date */}
      <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
        <Calendar className="w-3.5 h-3.5 text-gray-400" />
        <span>Gebietsstand: {edition.reference_date_display}</span>
      </div>

      {/* Dataset Tags */}
      <div className="flex flex-wrap gap-1 mb-3">
        <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
          GV100AD
        </span>
        {edition.includes_nad && (
          <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
            GV100NAD
          </span>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-gray-400 pt-2 border-t border-gray-100">
        <span>
          {edition.records_imported?.toLocaleString('de-DE') || '–'} Datensätze
        </span>
        <span>
          {edition.import_date
            ? new Date(edition.import_date).toLocaleDateString('de-DE')
            : 'Nicht importiert'}
        </span>
      </div>
    </div>
  )
}

// ============================================================================
// Editions Grid
// ============================================================================

function EditionsGrid({ onRefresh }: { onRefresh: number }) {
  const { data: editionsData, isLoading, refetch } = useEditions('GV100')
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    if (onRefresh > 0) {
      refetch()
    }
  }, [onRefresh, refetch])

  // Filter and sort editions
  const filteredEditions = useMemo(() => {
    if (!editionsData?.editions) return []

    let result = [...editionsData.editions]

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase()
      result = result.filter((ed) =>
        ed.edition.toLowerCase().includes(term) ||
        ed.reference_date_display.toLowerCase().includes(term) ||
        ed.dataset_name.toLowerCase().includes(term) ||
        String(ed.reference_year).includes(term)
      )
    }

    // Sort by reference_year descending
    result.sort((a, b) => b.reference_year - a.reference_year)

    return result
  }, [editionsData, searchTerm])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    )
  }

  if (!editionsData?.editions || editionsData.editions.length === 0) {
    return (
      <div className="text-center py-12">
        <Database className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p className="text-gray-500 mb-2">Keine GV100-Editionen importiert</p>
        <p className="text-sm text-gray-400">
          Klicken Sie auf "Neuer Import" um das Gemeindeverzeichnis zu importieren
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Edition oder Jahr suchen..."
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        {searchTerm && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
            {filteredEditions.length} von {editionsData.editions.length}
          </span>
        )}
      </div>

      {/* Grid */}
      {filteredEditions.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          Keine Ergebnisse für "{searchTerm}"
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredEditions.map((edition) => (
            <EditionCard key={edition.id} edition={edition} />
          ))}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Main Component
// ============================================================================

export function ImportWizard() {
  // Query-Parameter (aus der Updates-Seite): ?tab=destatis&year=YYYY öffnet den Import-Dialog vorbelegt
  const [searchParams] = useSearchParams()
  const presetTab = searchParams.get('tab') === 'destatis' ? 'destatis' : null
  const presetYearRaw = Number(searchParams.get('year'))
  const presetYear = Number.isInteger(presetYearRaw) && presetYearRaw > 0 ? presetYearRaw : null
  const [showImportDialog, setShowImportDialog] = useState(presetTab !== null)
  const [refreshKey, setRefreshKey] = useState(0)

  const handleImportComplete = () => {
    setRefreshKey((k) => k + 1)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">GV100</h1>
          <p className="text-gray-500 mt-1">
            Amtliches Gemeindeverzeichnis (destatis)
          </p>
        </div>
        <button
          onClick={() => setShowImportDialog(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Upload className="w-4 h-4" />
          Neuer Import
        </button>
      </div>

      {/* Editions Grid */}
      <EditionsGrid onRefresh={refreshKey} />

      {/* Import Dialog */}
      <ImportDialog
        isOpen={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        onImportComplete={handleImportComplete}
        initialTab={presetTab ?? 'destatis'}
        initialYear={presetYear}
      />
    </div>
  )
}
